/**
 * A template made in the app's tile builder, as a spec: what each tile shows,
 * not how Looker stores it. Shared by the server (which validates it again,
 * against the explore's real field list, and turns it into Looker writes) and
 * the builder page (which shows the same messages inline).
 *
 * Field names are bare ("spend", "date_week"); the server prefixes the view.
 * The workspace field is never a spec field: scopeQuery owns it.
 *
 * Pure, no I/O, relative imports only (api/ imports this file).
 *
 * @module services/looker/lookerTemplateSpec
 */
import { z } from 'zod';
import { APP_WIDGETS, RETIRED_WIDGETS } from './lookerAppTiles';

export const SPEC_VIS = [
  'single_value', 'line', 'area', 'column', 'bar', 'pie', 'donut', 'funnel', 'waterfall', 'scatter', 'table', 'app',
] as const;
export type SpecVis = (typeof SPEC_VIS)[number];

export const SPEC_MAX_TILES = 30;

/** What a vis needs, so the builder shows only the controls that apply. */
export interface VisShape {
  dimension: 'required' | 'optional' | 'none';
  pivot: boolean;
  /** Fewest and most measures; null = no upper bound. */
  measures: { min: number; max: number | null };
}

export const VIS_SHAPE: Record<SpecVis, VisShape> = {
  single_value: { dimension: 'none', pivot: false, measures: { min: 1, max: 1 } },
  pie: { dimension: 'required', pivot: false, measures: { min: 1, max: 1 } },
  donut: { dimension: 'required', pivot: false, measures: { min: 1, max: 1 } },
  funnel: { dimension: 'none', pivot: false, measures: { min: 2, max: null } },
  waterfall: { dimension: 'required', pivot: false, measures: { min: 1, max: 1 } },
  scatter: { dimension: 'required', pivot: false, measures: { min: 2, max: 2 } },
  line: { dimension: 'required', pivot: true, measures: { min: 1, max: null } },
  area: { dimension: 'required', pivot: true, measures: { min: 1, max: null } },
  column: { dimension: 'required', pivot: true, measures: { min: 1, max: null } },
  bar: { dimension: 'required', pivot: true, measures: { min: 1, max: null } },
  // A table needs at least one field of either kind (checked separately).
  table: { dimension: 'optional', pivot: true, measures: { min: 0, max: null } },
  // An app box is drawn by the app, not queried: no fields at all.
  app: { dimension: 'none', pivot: false, measures: { min: 0, max: 0 } },
};

const isWorkspaceField = (name: string): boolean => /workspace_id$/i.test(name);

/** A bare LookML field name: no view prefix, no filter syntax. */
export const SpecFieldName = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,63}$/, 'Not a field name')
  .refine((n) => !isWorkspaceField(n), 'The workspace field cannot be used');

const TileBase = z.object({
  key: z.string().regex(/^[a-z0-9][a-z0-9-]{0,31}$/, 'Use lowercase letters, digits and dashes'),
  title: z.string().trim().min(1, 'A tile needs a title').max(100),
  vis: z.enum(SPEC_VIS),
  measures: z.array(SpecFieldName).max(20),
  dimension: SpecFieldName.optional(),
  pivot: SpecFieldName.optional(),
  sort: z.object({ field: SpecFieldName, desc: z.boolean() }).optional(),
  limit: z.number().int().min(1).max(5000).optional(),
  extraDimensions: z.array(SpecFieldName).max(2).optional(),
  compare: z.boolean().optional(),
  compareMeasures: z.array(SpecFieldName).max(10).optional(),
  lineMeasures: z.array(SpecFieldName).max(20).optional(),
  rightAxis: z.array(SpecFieldName).max(20).optional(),
  autoGrain: z.boolean().optional(),
  shareOf: SpecFieldName.optional(),
  fadeBelow: z.object({ field: SpecFieldName, value: z.number().min(0).max(1e12) }).optional(),
  listAbsent: z.boolean().optional(),
  /** Show the tile only when the Objective filter holds exactly one objective (D1 box 3). */
  showWhen: z.literal('oneObjective').optional(),
  /** Which box the app draws (vis 'app' only). */
  widget: z.enum(APP_WIDGETS).optional(),
  width: z.enum(['sixth', 'third', 'half', 'full']),
});

/** Every field a tile reads, dimension first. */
export const tileFields = (t: { measures: string[]; dimension?: string; pivot?: string; extraDimensions?: string[] }): string[] => [
  ...(t.dimension ? [t.dimension] : []),
  ...(t.extraDimensions ?? []),
  ...(t.pivot ? [t.pivot] : []),
  ...t.measures,
];

/** Options only a Looker query reads; an app box takes none of them. */
const QUERY_OPTIONS = [
  'dimension', 'extraDimensions', 'pivot', 'sort', 'limit', 'compare', 'compareMeasures', 'lineMeasures', 'rightAxis',
  'autoGrain', 'shareOf', 'fadeBelow', 'listAbsent', 'showWhen',
] as const;

export const SpecTileSchema = TileBase.superRefine((t, ctx) => {
  const shape = VIS_SHAPE[t.vis];
  const issue = (path: string, message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  if (t.vis === 'app') {
    if (!t.widget) issue('widget', 'Choose a box');
    if (t.measures.length > 0) issue('measures', 'An app box takes no measures');
    for (const key of QUERY_OPTIONS) if (t[key] !== undefined) issue(key, 'An app box takes no query options');
    if (t.width === 'sixth') issue('width', 'An app box cannot be one sixth wide');
    return;
  }
  if (t.widget) issue('widget', 'Only an app box takes a widget');
  const n = t.measures.length;
  if (n < shape.measures.min || (shape.measures.max !== null && n > shape.measures.max)) {
    const { min, max } = shape.measures;
    issue('measures', max === null ? `Choose at least ${min} measure(s)` : min === max ? `Choose exactly ${min} measure(s)` : `Choose ${min}–${max} measures`);
  }
  if (new Set(t.measures).size !== n) issue('measures', 'A measure is chosen twice');
  if (shape.dimension === 'required' && !t.dimension) issue('dimension', 'Choose a dimension');
  if (shape.dimension === 'none' && t.dimension) issue('dimension', 'This chart type takes no dimension');
  if (t.pivot && !shape.pivot) issue('pivot', 'This chart type takes no pivot');
  if (t.pivot && t.pivot === t.dimension) issue('pivot', 'Pivot on a different field than the dimension');
  if (t.vis === 'table' && n === 0 && !t.dimension) issue('measures', 'Choose at least one field');
  if (t.sort && !tileFields(t).includes(t.sort.field)) issue('sort', 'Sort by one of the tile’s fields');
  const inMeasures = (xs: readonly string[] | undefined) => (xs ?? []).every((m) => t.measures.includes(m));
  const COMBO: readonly SpecVis[] = ['line', 'area', 'column'];
  if (t.width === 'sixth' && t.vis !== 'single_value') issue('width', 'Only a single value can be one sixth wide');
  if (t.compare && t.vis !== 'single_value' && t.vis !== 'table') issue('compare', 'Only a single value or a table can compare periods');
  if (t.compareMeasures?.length && (!t.compare || t.vis !== 'table')) issue('compareMeasures', 'Turn on compare for a table first');
  if (!inMeasures(t.compareMeasures)) issue('compareMeasures', 'Compare only measures the tile shows');
  if ((t.lineMeasures?.length || t.rightAxis?.length) && !COMBO.includes(t.vis)) issue(t.lineMeasures?.length ? 'lineMeasures' : 'rightAxis', 'Only line, area or column charts mix series');
  if (!inMeasures(t.lineMeasures)) issue('lineMeasures', 'Choose from the tile’s measures');
  if (!inMeasures(t.rightAxis)) issue('rightAxis', 'Choose from the tile’s measures');
  if (t.autoGrain && t.dimension !== 'date_date') issue('autoGrain', 'Weekly-when-long needs the Date dimension');
  if (t.shareOf && (t.vis !== 'table' || !t.measures.includes(t.shareOf))) issue('shareOf', 'Share needs a table and one of its measures');
  if (t.fadeBelow && (t.vis !== 'table' || !t.measures.includes(t.fadeBelow.field))) issue('fadeBelow', 'Fade needs a table and one of its measures');
  if (t.extraDimensions?.length) {
    const all = [t.dimension, t.pivot, ...t.extraDimensions].filter(Boolean);
    if (t.vis !== 'table' || !t.dimension) issue('extraDimensions', 'A second dimension needs a table with a first one');
    else if (new Set(all).size !== all.length) issue('extraDimensions', 'Each dimension once');
  }
  if (t.listAbsent && t.vis !== 'pie' && t.vis !== 'donut') issue('listAbsent', 'Only a pie or donut lists channels with no spend');
});
export type SpecTile = z.infer<typeof SpecTileSchema>;

/** True for a box the app draws itself (no Looker query). */
export const isAppTile = (t: Pick<SpecTile, 'vis'>): boolean => t.vis === 'app';

/** A stored app tile naming a retired widget: dropped before parsing, never an error. */
const isRetiredAppTile = (t: unknown): boolean => {
  if (typeof t !== 'object' || t === null) return false;
  const { vis, widget } = t as { vis?: unknown; widget?: unknown };
  return vis === 'app' && (RETIRED_WIDGETS as readonly unknown[]).includes(widget);
};

export const TemplateSpecSchema = z
  .object({
    title: z.string().trim().min(1, 'The template needs a title').max(100),
    description: z.string().max(500),
    tiles: z.preprocess(
      (v) => (Array.isArray(v) ? v.filter((t) => !isRetiredAppTile(t)) : v),
      z.array(SpecTileSchema).min(1, 'Add at least one tile').max(SPEC_MAX_TILES),
    ),
  })
  .superRefine((s, ctx) => {
    const seen = new Set<string>();
    s.tiles.forEach((t, i) => {
      if (seen.has(t.key)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tiles', i, 'key'], message: 'Two tiles have the same key' });
      seen.add(t.key);
    });
  });
export type TemplateSpec = z.infer<typeof TemplateSpecSchema>;

/** Looker's 24-column grid. */
const GRID_WIDTH = { sixth: 4, third: 8, half: 12, full: 24 } as const;
export const tileGridWidth = (t: Pick<SpecTile, 'width'>): number => GRID_WIDTH[t.width];
export const tileGridHeight = (t: Pick<SpecTile, 'vis'>): number => (t.vis === 'app' ? 4 : t.vis === 'single_value' ? 3 : 7);

/**
 * The Looker vis_config for each spec vis. Every type is one lookerTiles'
 * visTypeOf maps to a chart the app draws itself. Donut is a pie with a hole
 * (`looker_donut_multiples` would be several small donuts in Looker).
 */
export const LOOKER_VIS_CONFIG: Record<Exclude<SpecVis, 'app'>, Record<string, unknown>> = {
  single_value: { type: 'single_value' },
  line: { type: 'looker_line' },
  area: { type: 'looker_area' },
  column: { type: 'looker_column' },
  bar: { type: 'looker_bar' },
  pie: { type: 'looker_pie' },
  donut: { type: 'looker_pie', inner_radius: 50 },
  funnel: { type: 'looker_funnel' },
  waterfall: { type: 'looker_waterfall' },
  scatter: { type: 'looker_scatter' },
  table: { type: 'looker_grid' },
};
